import { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import './Page3346Style.css';
import ApiSender from '../common/ApiLogic';
import useOtpSendThrottle, { OTP_SEND_COOLDOWN_MS } from '../common/useOtpSendThrottle';
import { logger } from '../common/utils/logger';

function Page3346() {

  const [goalId, setGoalId] = useState(null);
  const [goalName, setGoalName] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const otpContainerRef = useRef();
  const [msisdn, setMsisdn] = useState(null);
  const [warning, setWarning] = useState(false);
  const [otp, setOtp] = useState(null);
  const [firstStep, setFirstStep] = useState(true);
  const [secondStep, setSecondStep] = useState(false);
  const [isMsisdnPopupVisible, setMsisdnPopupVisible] = useState(false);
  const [showSmsHint, setShowSmsHint] = useState(false);
  const otpThrottle = useOtpSendThrottle();
  const sendInFlightRef = useRef(false);

  const getInitialLang = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = (urlParams.get("lang") || "").toUpperCase();
    if (lang === "EN" || lang === "AR") return lang;

    // fallback: browser lang
    const navLang = (navigator.language || "en").toLowerCase();
    return navLang.startsWith("ar") ? "AR" : "EN";
  };

  const [language, setLanguage] = useState(getInitialLang);

  const { queryParams, paramsObject } = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const obj = Object.fromEntries(p.entries());
    return { queryParams: p, paramsObject: obj };
  }, [location.search]);

  const apiSenderRef = useRef(null);
  if (!apiSenderRef.current) apiSenderRef.current = new ApiSender();
  const apiSender = apiSenderRef.current;

  useEffect(() => {
    if (queryParams.has("campaignId")) {
      const fetchData = async () => {
        try {
          const response = await fetch(process.env.REACT_APP_GOOGLE_BASE_URL + 'campaign/info?uuid=' + queryParams.get("campaignId"),
            {
              method: 'GET',
              headers: {
                "Content-Type": "application/json"
              }
            });
          if (response.ok) {
            const data = await response.json();
            const status = data.status;
            if (status) {
              setGoalId(data.goalId);
              setGoalName(data.goalName);
            }
          }
        }
        catch (error) {
        }
      }
      fetchData();
    }
  }, []);

  const historyUrl = useMemo(
    () => window.location.origin + location.pathname,
    [location.pathname]
  );

  useEffect(() => {
    const run = () => apiSender.saveHistory(historyUrl, paramsObject);

    if ("requestIdleCallback" in window) requestIdleCallback(run);
    else setTimeout(run, 0);
  }, [apiSender, historyUrl, paramsObject]);

  useLayoutEffect(() => {
    if (secondStep) otpContainerRef.current?.focus();
  }, [secondStep]);

  const handleLanguage = () => {
    setLanguage((prev) => (prev === "EN" ? "AR" : "EN"));
  };

  const handleMsisdnChange = (e) => {
    setWarning(false);
    const next = e.target.value.replace(/\D/g, "").slice(0, 13); // digits only
    setMsisdn((prev) => (prev === next ? prev : next));
  };

  const openMsisdnPopup = () => {
    if (isMsisdnPopupVisible) return;
    setMsisdnPopupVisible(true);
  };

  const closeMsisdnPopup = () => {
    setMsisdnPopupVisible(false);
  };

  // Opens the MSISDN popup when the user clicks anywhere that isn't an
  // actual control, as long as we're still on the number-entry step and
  // the popup isn't already open.
  const handleBackgroundClick = (event) => {
    if (!firstStep || isMsisdnPopupVisible) return;
    if (event.target.closest('input, button, a, .language')) return;
    openMsisdnPopup();
  };

  const handleOtpChange = (e) => {
    setWarning(false);
    setShowSmsHint(false);
    const next = e.target.value.replace(/\D/g, "").slice(0, 4);
    setOtp((prev) => (prev === next ? prev : next));
  };

  const [sendLoading, setSendLoading] = useState(false);
  const [warningMsg, setWarningMsg] = useState("");
  const getParam = (k) => queryParams.get(k) || null;
  // Only 574/575 is Redbull; every other validly-formatted number is treated
  // as Mobily. Operator eligibility beyond that is enforced by the backend,
  // not the client.
  const getOperatorId = (m) => (isRedbull(m) ? 7 : 2);

  const handleSendOtpClick = async () => {
    // Rejects a second call while one is already in flight — the cooldown/
    // failure-count checks above only update after each API call resolves,
    // so overlapping rapid clicks (faster than the round-trip, or a tap on
    // both the main and popup Continue buttons) could otherwise all pass
    // isOnCooldown() before the first one's failure is even recorded.
    if (sendInFlightRef.current) return;
    if (otpThrottle.isOnCooldown()) {
      const secs = otpThrottle.cooldownSecondsLeft();
      setWarningMsg(language === "EN" ? `Too many attempts. Please try again in ${secs}s.` : `عدد كبير جدًا من المحاولات. يرجى المحاولة مرة أخرى خلال ${secs} ثانية.`);
      setWarning(true);
      return;
    }

    const invalidMsg = language === "EN" ? "Incorrect Number Format" : "رقم غير صحيح";
    const unsupportedMsg = language === "EN" ? "This mobile operator is not currently supported" : "شركة الاتصالات هذه غير مدعومة حاليًا";

    const normalizedMsisdn = normalizeMsisdn(msisdn);
    if (!isMsisdnValid(normalizedMsisdn)) {
      setWarningMsg(invalidMsg);
      setWarning(true);
      openMsisdnPopup();
      return;
    }

    if (!isKnownCarrier(normalizedMsisdn)) {
      setWarningMsg(unsupportedMsg);
      setWarning(true);
      return;
    }

    const operatorId = getOperatorId(normalizedMsisdn);

    sendInFlightRef.current = true;
    setSendLoading(true);

    let enteredCooldown = false;
    try {
      const result = await apiSender.sendPin(
        "send-pin",
        normalizedMsisdn,
        getParam("campaignId"),
        language,
        getParam("trackerId"),
        getParam("gclid"),
        getParam("gbraid"),
        getParam("wbraid"),
        goalId,
        goalName,
        window.location.origin + location.pathname,
        "LP AA3346",
        operatorId,
        paramsObject
      );

      if (result?.success) {
        otpThrottle.recordSuccess();
        if (result.redirect) {
          window.location.href = result.redirect;
          return;
        }
        setWarning(false);
        setWarningMsg("");
        setFirstStep(false);
        setSecondStep(true);
        closeMsisdnPopup();
        return;
      }

      enteredCooldown = otpThrottle.recordFailure();
      if (enteredCooldown) {
        logger.warn("send_otp", "throttled", `Send-OTP cooldown triggered for ${normalizedMsisdn} after repeated failures`, { msisdn: normalizedMsisdn, cooldownMs: OTP_SEND_COOLDOWN_MS });
      }
      setWarning(true);
      setWarningMsg(
        enteredCooldown
          ? (language === "EN" ? `Too many failed attempts. Please try again in ${otpThrottle.cooldownSecondsLeft()}s.` : `عدد كبير جدًا من المحاولات الفاشلة. يرجى المحاولة مرة أخرى خلال ${otpThrottle.cooldownSecondsLeft()} ثانية.`)
          : result?.message || (language === "EN" ? "Something went wrong" : "حدث خطأ")
      );
    } catch (e) {
      setWarning(true);
      setWarningMsg(language === "EN" ? "Network error, please try again" : "خطأ في الشبكة، حاول مرة أخرى");
    } finally {
      sendInFlightRef.current = false;
      if (enteredCooldown) {
        setTimeout(() => setSendLoading(false), OTP_SEND_COOLDOWN_MS);
      } else {
        setSendLoading(false);
      }
    }
  };

  const [verifyLoading, setVerifyLoading] = useState(false);


  const handleVerifyOtpClick = async () => {
    const o = (otp ?? "").trim();

    // Validate OTP (4 digits only)
    if (!/^\d{4}$/.test(o)) {
      setWarning(true);
      setWarningMsg(language === "EN" ? "Incorrect OTP" : "رمز سري غير صحيح");
      return;
    }

    const normalizedMsisdn = normalizeMsisdn(msisdn);
    const operatorId = getOperatorId(normalizedMsisdn);

    // Mobily special case: the user entered the SMS sender's shortcode instead of the OTP
    if (operatorId === 2 && o === "6071") {
      setWarning(true);
      setOtp("");
      setShowSmsHint(true);
      setWarningMsg(
        language === "EN"
          ? "That's the sender's number, not your code. Enter the 4-digit code you see in the SMS."
          : "هذا رقم المرسل وليس الرمز الخاص بك. أدخل الرمز المكوّن من 4 أرقام الذي تراه في الرسالة النصية."
      );
      otpContainerRef.current?.focus();
      return;
    }

    setVerifyLoading(true);

    try {
      const result = await apiSender.verifyPin(
        "verify-pin",
        normalizedMsisdn,
        o,
        getParam("campaignId"),
        language,
        getParam("trackerId"),
        getParam("gclid"),
        getParam("gbraid"),
        getParam("wbraid"),
        goalId,
        goalName,
        window.location.origin + location.pathname,
        "LP AA3346",
        operatorId
      );

      if (result?.success) {
        // if you want to show message on success page, pass it in state/query instead of warning div
        nav(`/aa3346/success?lang=${language}`);
        return;
      }

      setWarning(true);
      setOtp(""); // clear input on failure
      setWarningMsg(language === "EN" ? "Incorrect OTP. Please try again." : "رمز غير صحيح، يرجى المحاولة مرة أخرى.");
      otpContainerRef.current?.focus();
    } catch (e) {
      setWarning(true);
      setWarningMsg(language === "EN" ? "Network error, please try again" : "خطأ في الشبكة، حاول مرة أخرى");
    } finally {
      setVerifyLoading(false);
    }
  };

  // Auto-submit: once a valid, supported number is entered, click Continue automatically.
  const autoSendRef = useRef(null);
  useEffect(() => {
    if (!firstStep || !msisdn) return;
    const normalized = normalizeMsisdn(msisdn);
    if (!isMsisdnValid(normalized) || !isKnownCarrier(normalized)) return;
    if (autoSendRef.current === msisdn) return;
    autoSendRef.current = msisdn;
    const t = setTimeout(() => { handleSendOtpClick(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msisdn, firstStep]);

  // Auto-submit: once the 4-digit OTP is fully entered, click Verify automatically.
  const autoVerifyRef = useRef(null);
  useEffect(() => {
    if (!secondStep || !otp || otp.length !== 4) return;
    if (autoVerifyRef.current === otp) return;
    autoVerifyRef.current = otp;
    const t = setTimeout(() => { handleVerifyOtpClick(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, secondStep]);

  const isMsisdnValid = (m) => !!m && (m.length === 9 || m.length === 12);

  // Accepts 5XXXXXXXX, 05XXXXXXXX (leading 0), 9665XXXXXXXX / +9665XXXXXXXX /
  // 00966... (country code, with or without the leading 0) and normalizes all
  // of them down to a clean 9-digit core before re-adding the 966 prefix that
  // the rest of this file (and the API) expects.
  const normalizeMsisdn = (m) => {
    if (!m) return "";
    let digits = String(m).replace(/\D/g, "");
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (digits.startsWith("966")) digits = digits.slice(3);
    if (digits.startsWith("0")) digits = digits.slice(1);
    return digits.length === 9 ? `966${digits}` : digits;
  };

  const hasAnyPrefix = (value, prefixes) => prefixes.some((p) => value.startsWith(p));

  // Only 574/575 is Redbull; every other validly-formatted number is treated
  // as Mobily. Operator eligibility beyond that is enforced by the backend,
  // not the client.
  const isRedbull = (msisdn) => {
    const m = normalizeMsisdn(msisdn);
    return m.length === 12 && hasAnyPrefix(m, ["966574", "966575"]);
  };

  // Distinct from getOperatorId's broad "everything else is Mobily" backend
  // routing: this narrowly checks whether carrier billing has any real
  // chance of working, so we can skip send-pin entirely for numbers we
  // already know the backend can't handle gracefully (STC/Zain get a
  // confusing rejection message; other prefixes have been seen returning a
  // raw HTTP 500).
  const isKnownCarrier = (msisdn) => {
    const m = normalizeMsisdn(msisdn);
    return m.length === 12 && hasAnyPrefix(m, ["96654", "96656", "966574", "966575"]);
  };

  return (
    <div className="Aa3346" onClick={handleBackgroundClick}>
      <div className='container'>
        <div className='header'>
          <div className="language" onClick={handleLanguage}>
            <span>{language === 'EN' ? 'English' : 'عربي'}</span>
          </div>
        </div>
        <div className='form-content'>
          <div className='title'>
            <h4 id='headup-title'>{language === 'EN' ? 'Download is ready' : 'جاهز للتحميل'}</h4>
          </div>
          <div className='form-content-top'>
            <div className='logo'><img fetchpriority='high' alt='Ready' src='/tick.avif' decoding="async" width="96" height="96" /></div>
          </div>
          <div className='form-content-middle'>
            {firstStep && <div className="fieldShell">
              <span className="fieldLabel">
                <span className="fieldLabelText">{language === 'EN' ? 'Mobile Number' : 'رقم الموبايل'}</span>
              </span>
              <div className="fieldInner">
                <div className="fieldIcon" aria-hidden="true">
                </div>
                <input
                  id="msisdn-input"
                  className="fieldInput"
                  type="tel"
                  inputMode="numeric"
                  value={msisdn}
                  placeholder="5"
                  autoComplete="off"
                  autoFocus=""
                  onChange={handleMsisdnChange}
                  minLength={9}
                  maxLength={13}
                />
              </div>
            </div>}
            {secondStep && <div className="fieldShell">
              <span className="fieldLabel">
                <span className="fieldLabelText">{language === 'EN' ? 'Enter OTP' : 'رمز السري'}</span>
              </span>
              <div className="fieldInner">
                <input
                  id="otp-input"
                  className={`fieldInput ${warning ? "hasError" : ""}`}
                  type="tel"
                  inputMode="numeric"
                  value={otp}
                  autoComplete="one-time-code"
                  autoFocus=""
                  onChange={handleOtpChange}
                  minLength={4}
                  maxLength={4}
                  ref={otpContainerRef}
                />
              </div>
            </div>}
            {secondStep && showSmsHint && (
              <div className="otp-hint">
                <a className="open-sms-link" href="sms:607110">{language === 'EN' ? 'Open SMS to view your code' : 'افتح الرسائل لعرض الرمز'}</a>
              </div>
            )}
            <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`}>
              <span id='warning-msg'>{warningMsg || "\u00A0"}</span>
            </div>
            <div className='button'>
              {firstStep && <button id='send-otp-btn' onClick={handleSendOtpClick} disabled={sendLoading}>
                {language === 'EN' ? 'Continue' : 'تابع'}
                {sendLoading && <div id="loading-spinner-send" className="spinner" />}
              </button>}
              {secondStep && <button id='verify-otp-btn' onClick={handleVerifyOtpClick} disabled={verifyLoading}>
                {language === 'EN' ? 'Verify' : 'تأكيد'}
                {verifyLoading && <div id="loading-spinner-verify" className="spinner" />}
              </button>}
              <span>{language === 'EN' ? '' : ''}</span>
            </div>
          </div>
          <div className="form-content-bottom" dir={language === "EN" ? "ltr" : "rtl"}>
            <span>
              {language === "EN"
                ? "Kid Security is a parental-control app subscription billed directly through your mobile operator. For Mobily subscribers, the service costs 2.3 SAR per day (VAT included), auto-renewed daily until cancelled, with a 1-day free trial on your first subscription; VAT for prepaid lines is deducted from your recharge balance. For Redbull subscribers, the service costs 1.725 SAR per day (VAT included), auto-renewed daily until cancelled. By entering your mobile number and confirming the OTP code sent to it, you confirm you are the owner/authorized user of this number, agree to subscribe to this recurring paid service, and accept the Terms and Conditions. To cancel your Mobily subscription at any time, send U58 to 607110. To cancel your Redbull subscription, send CB517 to 143334. For help, contact support@kid-security.io."
                : "امان الأطفال هو اشتراك في تطبيق للرقابة الأبوية يُحصَّل مباشرة عبر شركة الاتصالات الخاصة بك. لمشتركي موبايلي: تبلغ تكلفة الخدمة 2.3 ريال سعودي يوميًا (شامل ضريبة القيمة المضافة)، ويتجدد الاشتراك تلقائيًا يوميًا حتى يتم إلغاؤه، مع تجربة مجانية لمدة يوم واحد عند أول اشتراك؛ بالنسبة لخطوط الدفع المسبق، تُخصم الضريبة من رصيد إعادة الشحن. لمشتركي ريد بول: تبلغ تكلفة الخدمة 1.725 ريال سعودي يوميًا (شامل ضريبة القيمة المضافة)، ويتجدد الاشتراك تلقائيًا يوميًا حتى يتم إلغاؤه. بإدخال رقم هاتفك المحمول وتأكيد رمز التحقق (OTP) المرسل إليه، فإنك تقر بأنك مالك/المستخدم المخوّل لهذا الرقم، وتوافق على الاشتراك في هذه الخدمة المدفوعة المتجددة، وتقبل الأحكام والشروط. لإلغاء اشتراك موبايلي في أي وقت، أرسل U58 إلى 607110. لإلغاء اشتراك ريد بول، أرسل CB517 إلى 143334. للمساعدة، تواصل معنا عبر support@kid-security.io."
              }
            </span>
          </div>
        </div>
      </div>
      {isMsisdnPopupVisible && (
        <div className='msisdn-popup-overlay'>
          <div className='msisdn-popup-box'>
            <p>{language === 'EN' ? 'Please enter your mobile number to continue' : 'الرجاء إدخال رقم هاتفك للمتابعة'}</p>
            <div className={`msisdn-popup-input ${warning ? 'hasError' : ''}`}>
              <input type='tel' inputMode='numeric' value={msisdn || ''} placeholder='5' autoComplete='off' minLength={9} maxLength={13} onChange={handleMsisdnChange} />
            </div>
            <button onClick={handleSendOtpClick}>{language === 'EN' ? 'Continue' : 'تابع'}</button>
          </div>
          <button className='popup-exit-btn' onClick={closeMsisdnPopup}>{language === 'EN' ? 'Close' : 'إغلاق'}</button>
        </div>
      )}
    </div >
  );
}

export default Page3346;